import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { Avatar, Tooltip } from 'antd';

import { useAuth } from '@/hooks/useAuth';
import { getAccountInfo } from '@/services/usageService';
import type { AccountInfo } from '@/services/usageService';
import { isInternalDomain } from '@/utils/const';
import { NavBar } from '@/v2/components/NavBar';

export default function Dashboard() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { user, loading, error, isAuthenticated } = useAuth();
  const [accountInfo, setAccountInfo] = useState<AccountInfo | null>(null);
  const emailRef = useRef<HTMLDivElement>(null);
  const [isEmailOverflow, setIsEmailOverflow] = useState(false);

  // 外部域名：如果未登录，跳转到登录页面
  useEffect(() => {
    if (!loading && !isAuthenticated && !isInternalDomain()) {
      // 外部域名：跳转到登录页面
      sessionStorage.setItem('pendingRedirect', '/dashboard');
      navigate('/login?redirect=/dashboard');
    }
  }, [loading, isAuthenticated, navigate]);

  // 获取账户信息（仅在用户认证成功后执行，或切换到 /dashboard/usage 时刷新）
  useEffect(() => {
    // 只在用户已认证且有用户信息时才获取账户信息
    if (!isAuthenticated || !user) {
      return;
    }

    const fetchAccountInfo = async () => {
      try {
        const response = await getAccountInfo();
        if (response.status === 200 && response.data) {
          setAccountInfo(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch account info:', error);
      }
    };

    fetchAccountInfo();
  }, [isAuthenticated, user, pathname]);

  // 检测邮箱文本是否溢出
  useEffect(() => {
    if (!emailRef.current) return;

    const checkOverflow = () => {
      const isOverflowing = emailRef.current!.scrollWidth > emailRef.current!.clientWidth;
      setIsEmailOverflow(isOverflowing);
    };

    // 使用 ResizeObserver 监听元素尺寸变化
    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(emailRef.current);

    // 初始检查
    checkOverflow();

    return () => {
      resizeObserver.disconnect();
    };
  }, [user?.email, user?.mail]);

  // 如果正在加载，显示加载状态
  if (loading) {
    return (
      <div>
        <NavBar />
        <div className="flex min-h-screen items-center justify-center bg-[#000000]">
          <div className="flex flex-col items-center gap-4">
            <div className="size-8 animate-spin rounded-full border-b-2 border-[#326BFB]" />
          </div>
        </div>
      </div>
    );
  }

  // 如果未认证，不渲染内容（应该已经跳转到登录页）
  if (!isAuthenticated || !user) {
    return null;
  }

  // 如果有错误，显示错误信息（但不阻止渲染，因为可能是网络错误）
  if (error) {
    console.error('认证错误:', error);
  }

  return (
    <div>
      <NavBar />
      <div className="flex min-h-screen flex-col items-center gap-[45px] bg-[#ffffff] px-4 py-[104px] md:px-[120px]">
        <div className="flex w-full max-w-[1200px] flex-col gap-[16px] md:flex-row md:gap-[70px] md:px-0">
          {/* 左侧用户信息卡片 */}
          <div className="flex w-full flex-col md:w-[240px]">
            {/* 用户信息 */}
            <div className="flex min-h-[38px] w-full gap-[8px]">
              <div className="size-[38px]">
                <Avatar
                  src={user.avatar}
                  size={38}
                  shape="square"
                  style={{
                    borderRadius: '4px',
                    border: 'none',
                    background: '#29E2CC',
                    fontSize: '16px',
                    lineHeight: '16px',
                    fontWeight: 600,
                    color: '#32EDDA',
                  }}
                >
                  {user.displayName?.charAt(0).toUpperCase() || 'U'}
                </Avatar>
              </div>
              <div className="flex flex-col">
                <div className="flex min-h-[20px] items-center gap-[8px]">
                  <span className="text-base font-bold leading-[20px] text-[#111314]">
                    {user.displayName || user.username}
                  </span>
                  <div className="flex h-[19px] items-center justify-center rounded bg-[#111314] px-[6px]">
                    <span
                      className="text-[12px] font-semibold leading-[130%] tracking-[5%] text-[#ffffff]"
                      style={{
                        fontFamily: 'Manrope',
                      }}
                    >
                      {accountInfo?.account.tierName || 'Free'}
                    </span>
                  </div>
                </div>
                {isEmailOverflow ? (
                  <Tooltip title={user.email || user.mail} placement="bottom">
                    <div
                      ref={emailRef}
                      className="min-h-[18px] w-full truncate text-[14px] font-normal leading-[130%] tracking-normal text-[#111314] opacity-50 md:max-w-[197px]"
                      style={{
                        fontFamily: 'Manrope',
                      }}
                    >
                      {user.email || user.mail}
                    </div>
                  </Tooltip>
                ) : (
                  <div
                    ref={emailRef}
                    className="min-h-[18px] w-full truncate text-[14px] font-normal leading-[130%] tracking-normal text-[#111314] opacity-50 md:max-w-[197px]"
                    style={{
                      fontFamily: 'Manrope',
                    }}
                  >
                    {user.email || user.mail}
                  </div>
                )}
              </div>
            </div>

            {/* Tab 按钮组 */}
            <div className="mt-6 flex w-[245px] flex-col items-start self-stretch">
              {/* 积分用量 */}
              {/* 积分用量 & 计划管理 - 仅对外部用户显示 */}
              {!isInternalDomain() && (
                <>
                  <div className="flex h-12 w-[245px] flex-col items-start justify-center self-stretch">
                    <NavLink
                      to="/dashboard/usage"
                      className={({ isActive }) =>
                        `flex h-[42px] w-[245px] items-center self-stretch rounded-lg px-4 py-2.5 no-underline transition-colors duration-200 ${
                          isActive ? 'bg-[#F7F7F7]' : 'bg-transparent hover:bg-[#F7F7F7]/50'
                        }`
                      }
                    >
                      {({ isActive }) => (
                        <span
                          className={`flex h-[22px] min-w-[140px] items-center text-sm leading-[22px] text-[#252626] ${
                            isActive ? 'font-medium' : 'font-normal'
                          }`}
                        >
                          {t('usageTab.navUsage')}
                        </span>
                      )}
                    </NavLink>
                  </div>

                  <div className="flex h-12 w-[245px] flex-col items-start justify-center self-stretch">
                    <NavLink
                      to="/dashboard/plan"
                      className={({ isActive }) =>
                        `flex h-[42px] w-[245px] items-center self-stretch rounded-lg px-4 py-2.5 no-underline transition-colors duration-200 ${
                          isActive ? 'bg-[#F7F7F7]' : 'bg-transparent hover:bg-[#F7F7F7]/50'
                        }`
                      }
                    >
                      {({ isActive }) => (
                        <span
                          className={`flex h-[22px] min-w-[140px] items-center text-sm leading-[22px] text-[#252626] ${
                            isActive ? 'font-medium' : 'font-normal'
                          }`}
                        >
                          {t('usageTab.navPlan')}
                        </span>
                      )}
                    </NavLink>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* 右侧内容区域 */}
          <div className="w-full flex-1">
            <Outlet context={{ accountInfo }} />
          </div>
        </div>
      </div>
    </div>
  );
}
